import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    key: '',
    description: '',
    status: 'active',
  });
  const [currentUser, setCurrentUser] = useState(null);

  const navigate = useNavigate();

  // Roles that can create/edit/archive projects globally
  const CAN_MANAGE_PROJECTS = ['admin', 'qa_manager', 'qa_lead'];
  const canManageProjects = currentUser && CAN_MANAGE_PROJECTS.includes(currentUser.role);

  useEffect(() => {
    fetchCurrentUser();
    fetchProjects();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentUser(data.data?.user || data.data || data.user || data);
      }
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjects(data.data || []);
      } else if (response.status === 401) {
        navigate('/login');
      } else {
        setError('Failed to fetch projects');
      }
    } catch (err) {
      setError('Error loading projects');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('token');
      const url = editingProject
        ? `${API_URL}/projects/${editingProject.id || editingProject._id}`
        : `${API_URL}/projects`;
      const method = editingProject ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        setShowModal(false);
        setEditingProject(null);
        setFormData({ name: '', key: '', description: '', status: 'active' });
        fetchProjects();
      } else {
        setError(data.error || 'Failed to save project');
      }
    } catch (err) {
      setError('Error saving project');
      console.error(err);
    }
  };

  const handleEdit = project => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      key: project.key,
      description: project.description || '',
      status: project.status,
    });
    setShowModal(true);
  };

  const handleDelete = async projectId => {
    if (!window.confirm('Are you sure you want to archive this project?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/projects/${projectId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchProjects();
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete project');
      }
    } catch (err) {
      alert('Error deleting project');
      console.error(err);
    }
  };

  const openCreateModal = () => {
    setEditingProject(null);
    setFormData({ name: '', key: '', description: '', status: 'active' });
    setShowModal(true);
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <div>
            <h1 className="page-title">Projects</h1>
            <p className="page-subtitle">Manage your QA projects</p>
          </div>
        </div>
        <div className="zq-spinner-wrap">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Projects</h1>
          <p className="page-subtitle">Manage your QA projects</p>
        </div>
        {canManageProjects && (
          <button className="btn btn-primary" onClick={openCreateModal}>
            + New Project
          </button>
        )}
      </div>

      <div className="container-fluid">
        {error && <div className="alert alert-danger mb-3">{error}</div>}

        <div className="row g-3">
          {projects.map(project => (
            <div key={project.id || project._id} className="col-md-4 col-lg-3">
              <div className="card h-100" style={{ transition: 'box-shadow .18s, transform .18s' }}
                onMouseEnter={e => { e.currentTarget.style.boxShadow = 'var(--zq-shadow)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.boxShadow = ''; e.currentTarget.style.transform = ''; }}
              >
                <div className="card-body d-flex flex-column">
                  <div className="d-flex align-items-start justify-content-between mb-3">
                    <div
                      style={{
                        width: 40, height: 40, borderRadius: 10,
                        background: 'var(--zq-primary-light)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 800, fontSize: 15, color: 'var(--zq-primary)',
                        flexShrink: 0,
                      }}
                    >
                      {(project.key || '?').slice(0, 2)}
                    </div>
                    <span
                      className={`badge ${project.status === 'active' ? 'bg-success' : 'bg-warning'}`}
                      style={{ fontSize: '11px' }}
                    >
                      {project.status}
                    </span>
                  </div>

                  <h6 className="mb-1" style={{ fontWeight: 700, fontSize: '14.5px', color: 'var(--zq-text-primary)' }}>
                    {project.name}
                  </h6>
                  <span style={{ fontSize: '11.5px', color: 'var(--zq-text-muted)', fontFamily: 'monospace', marginBottom: 6 }}>
                    {project.key}
                  </span>
                  {project.description && (
                    <p style={{ fontSize: '13px', color: 'var(--zq-text-secondary)', marginBottom: 0, flex: 1 }}>
                      {project.description}
                    </p>
                  )}
                </div>
                <div className="card-footer d-flex gap-2">
                  <button
                    className="btn btn-primary btn-sm flex-fill"
                    onClick={() => navigate(`/projects/${project._id || project.id}`)}
                  >
                    Open
                  </button>
                  {canManageProjects && (
                    <>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleEdit(project)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDelete(project.id || project._id)}
                        title="Archive"
                      >
                        🗄️
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {projects.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📁</div>
            {canManageProjects ? (
              <>
                <p>No projects yet. Create your first project to get started!</p>
                <button className="btn btn-primary" onClick={openCreateModal}>+ New Project</button>
              </>
            ) : (
              <p>You have no projects assigned yet. Ask an admin to add you to a project.</p>
            )}
          </div>
        )}
      </div>

      {/* Project Form Modal */}
      {showModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(15,23,42,.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingProject ? 'Edit Project' : 'New Project'}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close"
                ></button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="modal-body">
                  {error && <div className="alert alert-danger">{error}</div>}
                  <div className="mb-3">
                    <label className="form-label">Project Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Project Key *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.key}
                      onChange={e => setFormData({ ...formData, key: e.target.value.toUpperCase() })}
                      pattern="[A-Z0-9]{2,10}"
                      maxLength="10"
                      required
                      disabled={editingProject}
                      placeholder="e.g., PROJ, QA, TEST"
                    />
                    <div className="form-text">2–10 uppercase characters. Cannot be changed after creation.</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={formData.description}
                      onChange={e => setFormData({ ...formData, description: e.target.value })}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={e => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-secondary" onClick={() => setShowModal(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    {editingProject ? 'Save Changes' : 'Create Project'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;
